import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import classnames from "classnames/bind";

import Preloader from "@components/preloader";
import Icon from "@components/icon";
import Tooltip from "@components/tooltip";

import pluralize from "ati-utils/pluralize";

import NotificationsScrollbar from "../NotificationsScrollbar/NotificationsScrollbar";
import PushNotificationContainer from "../PushNotificationContainer/PushNotificationContainer";
import NotificationErrorPanel from "../NotificationErrorPanel";
import PaigerAd from "../PaigerAd";

import getAndroidVersion from "../../../../utils/getAndroidVersion";
import { sendNavigationMetric } from "../../../../utils/sendMetric";

import { useStore } from "../../../../connect";

import strings from "../../../../locales/strings";

import styles from "./NotificationsList.scss";

const cx = classnames.bind(styles);

const androidVersion = getAndroidVersion();

const soundData = {
  on: {
    tooltip: strings.soundMute,
    label: "sound",
  },
  off: {
    tooltip: strings.soundOn,
    label: "mute",
  },
};

const NotificationsList = ({ mobile, notificationSubmenu }) => {
  const { notifications, baseUrl, profile, locale } = useStore();

  const notificationsList = useRef(null);

  useEffect(() => {
    notifications.handleNotificationsMount();

    return () => {
      notifications.handleNotificationsUnmount();
    };
  }, []);

  const emptyNotifications = () => {
    const { notificationsTotal } = notifications;

    const label = pluralize(
      strings.notificationsPluralize,
      notificationsTotal,
      locale
    );

    return (
      <div
        className={cx("empty-notifications-panel", {
          "old-android": androidVersion && androidVersion < 4.6,
        })}
      >
        <div className={cx("message-empty")}>
          <p>
            {notificationsTotal
              ? `${notificationsTotal} ${label}`
              : strings.noNewNotifications}
          </p>
        </div>

        <PaigerAd />
      </div>
    );
  };

  const readAllNotifications = (event) => {
    const { markAllNotificationsAsRead, notificationsNumber } = notifications;

    event.preventDefault();
    event.stopPropagation();

    if (notificationsNumber && markAllNotificationsAsRead) {
      localStorage.setItem(
        "atiNotificationsNumber",
        JSON.stringify({
          value: 0,
          updated: Date.now(),
        })
      );
      markAllNotificationsAsRead();
    }
  };

  const handleShowRegularNotifications = () =>
    notifications.onShowRegularNotifications &&
    setTimeout(() => notifications.onShowRegularNotifications(), 0);

  const getHeadForPushNotification = () => (
    <button
      className={cx("goto-regular-notifiactions", "glz-link", "glz-is-pointer")}
      onClick={handleShowRegularNotifications}
    >
      {notifications.notificationsNumber
        ? `${strings.viewUnread} (${notifications.notificationsNumber})`
        : strings.readAll}
    </button>
  );

  const getHeadForRegularNotifications = () => {
    const {
      markAllNotificationsAsRead,
      notificationsNumber,
      extraNewNotifications,
    } = notifications;

    return (
      !!markAllNotificationsAsRead && (
        <button
          className={cx("checked-notifications", {
            "checkbox-disabled": extraNewNotifications,
            read: !notificationsNumber,
          })}
          onClick={readAllNotifications}
        >
          <Icon name="ic_check-thin" size="s" />
          <span className={cx("notifications-checkbox-text")}>
            {notificationsNumber
              ? `${strings.markAllAsRead} (${notificationsNumber})`
              : strings.allRead}
          </span>
        </button>
      )
    );
  };

  const getBodyForPushNotification = () => (
    <PushNotificationContainer mobile={mobile} />
  );

  const getBodyForRegularNotifications = () =>
    notifications.notifications.length ? (
      <NotificationsScrollbar mobile={mobile} baseUrl={baseUrl} />
    ) : (
      emptyNotifications()
    );

  const sound =
    soundData[notifications.isNotificationSoundActive ? "on" : "off"];

  return (
    <div
      className={cx("noification-menu", {
        "noification-submenu": notificationSubmenu,
      })}
      data-name="notification-dropdown-menu"
      ref={notificationsList}
    >
      <div className={cx("notification-header")}>
        <div className={cx("settings-block")}>
          {notifications.showNotificationFromPush
            ? getHeadForPushNotification()
            : getHeadForRegularNotifications()}
          <div className={cx("sound-and-cog")}>
            <Tooltip
              disabled={window.innerWidth < 1024}
              isWithoutWrapper
              content={sound.tooltip}
            >
              <button
                className={cx("sound-icon")}
                onClick={notifications.toggleSound}
              >
                {sound.label === "mute" ? (
                  <Icon name="ic_sound-off" size="s" />
                ) : (
                  <Icon name="ic_sound" size="s" />
                )}
              </button>
            </Tooltip>

            <Tooltip
              disabled={window.innerWidth < 1024}
              isWithoutWrapper
              content={strings.notificationSettings}
            >
              <a
                href={`https://${baseUrl}/notification-settings/?contactId=${profile.user?.contact.id}?utm_source=header&utm_campaign=new_header`}
                className={cx("spin-cog")}
                data-name="settings-button"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => sendNavigationMetric("NOTIFICATION_SETTINGS")}
              >
                <Icon name="ic_cog" size="s" />
              </a>
            </Tooltip>
          </div>
        </div>
        <div className={cx("breaker")} />
      </div>

      <div className={cx("scrollbar-wrapper")}>
        <NotificationErrorPanel />
        {notifications.loadingNotifications ? (
          <div
            className={cx("load-notifications-spinner")}
            data-name="notifications-preloader"
          >
            <Preloader />
          </div>
        ) : (
          <div>
            {notifications.showNotificationFromPush
              ? getBodyForPushNotification()
              : getBodyForRegularNotifications()}
          </div>
        )}
      </div>
    </div>
  );
};

NotificationsList.propTypes = {
  notificationSubmenu: PropTypes.bool,
  mobile: PropTypes.bool,
};

export default observer(NotificationsList);
